/*
 * Component Stylesheet Guidelines:
 *
 * 1. In accordance with Theme System individual component stylesheets:
 *    - SHOULD NOT have any hardcoded rgba/hex colors
 *    - MUST use theme variables for all colors
 *    - CAN define new gradients using theme colors
 *
 * 2. In accordance with the Typography System individual component stylesheets:
 *    - SHOULD NOT define font sizes or weights directly
 *    - MUST use typography variables for text styling
 *    - Handles text hierarchy (H1, H2, SubHeading, etc.)
 *
 * 3. For Layout Control individual stylesheets:
 *    - CAN control alignment and layout of children
 *    - Parent components responsible for thier first level child positioning and alignment
 *
 * SocialFooter Component Styles:
 * This stylesheet manages the layout and positioning of the social footer:
 * - Footer container positioning and dimensions
 * - Logo and navigation item alignment
 * - Responsive layout adjustments
 * - Interactive state animations
 *
 * All text styling is delegated to Typography component
 * All colors are pulled from theme variables
 */

import React from 'react';
import Typography from '../../basic/Typography';
import './SocialFooter.css';

const SOCIALS = [
  { href: 'https://twitter.com', icon: 'fab fa-twitter', label: 'Twitter' },
  { href: 'https://linkedin.com', icon: 'fab fa-linkedin-in', label: 'LinkedIn' },
  { href: 'https://facebook.com', icon: 'fab fa-facebook-f', label: 'Facebook' },
  { href: 'https://instagram.com', icon: 'fab fa-instagram', label: 'Instagram' },
  { href: 'https://youtube.com', icon: 'fab fa-youtube', label: 'YouTube' },
];

const NAV_LINKS = [
  { text: 'Careers', href: '#' },
  { text: 'About', href: '#' },
  { text: 'Blog', href: '/blog' },
];

const CITIES = [
  'San Francisco',
  'Los Angeles',
  'Mumbai',
];

interface SocialFooterProps {
  companyName: string;
}

const SocialFooter: React.FC<SocialFooterProps> = ({ companyName }) => (
  <footer className="social-footer">
    <div className="footer-content">
      <div className="footer-header-row">
        <div className="footer-logo-row">
          <button
            className="footer-logo-link"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Scroll to top"
            tabIndex={0}
            style={{ background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <img src="/news/demo/public/assets/logo.png" alt="vMonad Logo" className="footer-logo" />
            <Typography variant="h3" className="footer-brand">
              {companyName}
            </Typography>
          </button>
        </div>
        <div className="footer-socials">
          {SOCIALS.map(s => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}>
              <i className={s.icon} />
            </a>
          ))}
        </div>
      </div>
      <div className="footer-main-row">
        <div className="footer-cta">
          <Typography variant="h2" className="footer-cta-title">
            AI First Studio
          </Typography>
          <Typography variant="body1" color="secondary" className="footer-cta-subtitle">
            Agentic Media Platform
          </Typography>
          <a
            href="mailto:queries@vmonad.com"
            className="footer-contact-btn apple-style-button"
          >
            <Typography variant="body1" color="primary">
              Contact Us
            </Typography>
          </a>
        </div>
        <div className="footer-links">
          <ul>
            {NAV_LINKS.map(link => (
              <li key={link.text}>
                <a href={link.href}>
                  <Typography variant="body2" color="secondary">
                    {link.text}
                  </Typography>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="footer-skyline-row">
        <img src="/game/cityscape.svg" alt="Cityscape" className="footer-skyline" />
        <div className="footer-cities">
          {CITIES.map(city => (
            <Typography key={city} variant="body2" color="secondary">
              {city}
            </Typography>
          ))}
        </div>
      </div>
      <div className="footer-bottom-row">
        <Typography variant="caption" color="secondary" className="footer-copyright">
          &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
        </Typography>
      </div>
    </div>
  </footer>
);

export default SocialFooter;
